import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarPlusIcon, CheckIcon, CreditCardIcon, IdCardIcon, PlusIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { participants, type Activity } from '../../data/loisirs';
interface RegistrationFlowProps {
  activity: Activity | null;
  onClose: () => void;
}
const steps = ['Participant', 'Activité', 'Identité', 'Paiement', 'Confirmation'];
export function RegistrationFlow({
  activity,
  onClose
}: RegistrationFlowProps) {
  const [step, setStep] = useState(0);
  const [participant, setParticipant] = useState(participants[1].id);
  const close = () => {
    onClose();
    setStep(0);
  };
  const selected = participants.find(person => person.id === participant);
  return <AnimatePresence data-id="element-2289" data-mp="1wd9yefsxu2z5">
      {activity && <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-2290" data-mp="1wel3qk217isd">
          <button type="button" aria-label="Fermer" onClick={close} className="absolute inset-0 bg-ville-navy/40" data-id="element-2291" data-mp="35vk5482d840" />

          <motion.div role="dialog" aria-modal="true" aria-label={`Inscription à ${activity.name}`} initial={{
        y: 24,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 16,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8" data-id="element-2292" data-mp="3pf6ld76ti2co">
            <div className="flex items-start justify-between gap-4" data-id="element-2293" data-mp="31pkjud2z3b1i">
              <div data-id="element-2294" data-mp="31nhn7a7bejnt">
                <p className="text-xs font-bold uppercase tracking-wider text-ville-indigo" data-id="element-2295" data-mp="2wqhfyvyqbav4">
                  Étape {Math.min(step + 1, steps.length)} de {steps.length}
                </p>
                <h2 className="mt-1 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2296" data-mp="1op7p6dhtg92m">{activity.name}</h2>
              </div>
              <button type="button" onClick={close} aria-label="Fermer" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2297" data-mp="1ete4u7xuvbaa">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-2298" data-mp="w2myzgd2u30n" />
              </button>
            </div>

            <div className="mt-6" data-id="element-2299" data-mp="110y7nnrmy7ha">
              {step === 0 && <div data-id="element-2300" data-mp="10z3ly78jibc5">
                  <h3 className="text-base font-bold text-ville-navy" data-id="element-2301" data-mp="m4gyn36x54xw">Pour qui ?</h3>
                  <ul className="mt-4 flex flex-wrap gap-3" data-id="element-2302" data-mp="r3ac4vm7gvy2">
                    {participants.map(person => {
                const active = person.id === participant;
                return <li key={person.id} data-id="element-2303" data-mp="3g6gt0hd5l9x">
                          <button type="button" aria-pressed={active} onClick={() => setParticipant(person.id)} className={`flex w-24 flex-col items-center gap-2 rounded-2xl border px-3 py-4 transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200 hover:border-ville-indigo'}`} data-id="element-2304" data-mp="3hhj0vv5ukyx">
                            <span className={`grid h-12 w-12 place-items-center rounded-full text-sm font-bold ${active ? 'bg-ville-indigo text-white' : 'bg-slate-100 text-ville-navy'}`} aria-hidden="true" data-id="element-2305" data-mp="v7uoh9p75jmf">
                              {person.initials}
                            </span>
                            <span className="text-sm font-bold text-ville-navy" data-id="element-2306" data-mp="38e2hu3t3i5fx">{person.name}</span>
                            <span className="text-xs text-slate-500" data-id="element-2307" data-mp="3dcm2m0vydb6p">{person.detail}</span>
                          </button>
                        </li>;
              })}
                    <li data-id="element-2308" data-mp="2knyb3nkm1pkp">
                      <button type="button" className="flex w-24 flex-col items-center gap-2 rounded-2xl border border-dashed border-slate-300 px-3 py-4 text-slate-500 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2309" data-mp="2kp9gfrtpf5dx">
                        <span className="grid h-12 w-12 place-items-center rounded-full bg-slate-50" aria-hidden="true" data-id="element-2310" data-mp="9v3sfmspjs34">
                          <PlusIcon className="h-5 w-5" data-id="element-2311" data-mp="1udedjkxrjs7g" />
                        </span>
                        <span className="text-sm font-bold" data-id="element-2312" data-mp="1pfmnyxklci6f">Ajouter</span>
                        <span className="text-xs" data-id="element-2313" data-mp="1knmg8iya4kza">un membre</span>
                      </button>
                    </li>
                  </ul>
                </div>}

              {step === 1 && <dl className="space-y-3 rounded-2xl border border-slate-200 p-5 text-sm" data-id="element-2314" data-mp="1ar9rlzhlcxbe">
                  <div className="flex justify-between gap-4" data-id="element-2315" data-mp="16nk1v6p6k9os">
                    <dt className="text-slate-500" data-id="element-2316" data-mp="11pimavqyqsi2">Activité</dt>
                    <dd className="font-bold text-ville-navy" data-id="element-2317" data-mp="11pscokfx71oa">{activity.name}</dd>
                  </div>
                  <div className="flex justify-between gap-4" data-id="element-2318" data-mp="wsa3csxzqkfr">
                    <dt className="text-slate-500" data-id="element-2319" data-mp="2w2i50xqn42tk">Horaire</dt>
                    <dd className="font-bold text-ville-navy" data-id="element-2320" data-mp="2r4glyct7s3i3">{activity.schedule}</dd>
                  </div>
                  <div className="flex justify-between gap-4" data-id="element-2321" data-mp="pfkipffe7iq6">
                    <dt className="text-slate-500" data-id="element-2322" data-mp="pd7rebnqvw0g">Lieu</dt>
                    <dd className="font-bold text-ville-navy" data-id="element-2323" data-mp="u72k24lbvlbo">{activity.place}</dd>
                  </div>
                  <div className="flex justify-between gap-4" data-id="element-2324" data-mp="fifuzb5wff79">
                    <dt className="text-slate-500" data-id="element-2325" data-mp="kfy5h5zke2hd">Participant</dt>
                    <dd className="font-bold text-ville-navy" data-id="element-2326" data-mp="ke3kjroamada">{selected?.name}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-t border-slate-100 pt-3" data-id="element-2327" data-mp="183xvu4syrqvf">
                    <dt className="text-slate-500" data-id="element-2328" data-mp="1d2smfsx6ajq3">Prix</dt>
                    <dd className="text-base font-extrabold text-ville-navy" data-id="element-2329" data-mp="y6kw0av68m2d">{activity.price}</dd>
                  </div>
                </dl>}

              {step === 2 && <div className="space-y-3" data-id="element-2330" data-mp="3ixk5t47fkzxh">
                  {[{
              icon: IdCardIcon,
              label: 'Carte numérique vérifiée',
              detail: 'Carte citoyenne de Charlotte Bouchard'
            }, {
              icon: ShieldCheckIcon,
              label: 'Résidence confirmée',
              detail: '4590, rue Bourgogne, Brossard'
            }, {
              icon: CheckIcon,
              label: 'Admissibilité validée',
              detail: `${selected?.name} · ${activity.audience}`
            }].map(item => <div key={item.label} className="flex items-start gap-3 rounded-2xl bg-slate-50 px-4 py-3" data-id="element-2331" data-mp="2aynydxbu5lwp">
                      <item.icon className="mt-0.5 h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-2332" data-mp="1n8ag2yj1z1fb" />
                      <div data-id="element-2333" data-mp="1iaid99gvduwc">
                        <p className="text-sm font-bold text-ville-navy" data-id="element-2334" data-mp="1ibaaspu122p9">{item.label}</p>
                        <p className="text-sm text-slate-500" data-id="element-2335" data-mp="1w1x6sa8elehn">{item.detail}</p>
                      </div>
                    </div>)}
                  <p className="text-sm text-slate-500" data-id="element-2336" data-mp="13kt7f0xu12pq">
                    Aucune information à saisir de nouveau : tout provient de votre profil citoyen.
                  </p>
                </div>}

              {step === 3 && <div data-id="element-2337" data-mp="1ofbn52qibj08">
                  <div className="flex items-center gap-4 rounded-2xl border border-ville-indigo bg-ville-indigo-soft px-5 py-4" data-id="element-2338" data-mp="1og3ajq9yaaff">
                    <CreditCardIcon className="h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-2339" data-mp="1xeygr2czvkiq" />
                    <div className="min-w-0 flex-1" data-id="element-2340" data-mp="22djemv4rxs40">
                      <p className="text-sm font-bold text-ville-navy" data-id="element-2341" data-mp="22f2yevpn2laj">Visa •••• 4127</p>
                      <p className="text-sm text-slate-600" data-id="element-2342" data-mp="27ccwezr3g59z">Moyen de paiement enregistré</p>
                    </div>
                    <span className="text-base font-extrabold text-ville-navy" data-id="element-2343" data-mp="2c9uvlyfb764s">{activity.price}</span>
                  </div>
                  <p className="mt-3 text-sm text-slate-500" data-id="element-2344" data-mp="2l49ion62zkyg">
                    Votre facture sera déposée dans Mon profil → Mes factures.
                  </p>
                </div>}

              {step === 4 && <div className="text-center" data-id="element-2345" data-mp="2v3ibhcp7mtj8">
                  <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-2346" data-mp="2v0cihmdgn7i8">
                    <CheckIcon className="h-7 w-7" data-id="element-2347" data-mp="3069dkio31iv9" />
                  </span>
                  <h3 className="mt-4 text-xl font-extrabold text-ville-navy" data-id="element-2348" data-mp="2v7y6ugt33oh9">Inscription confirmée</h3>
                  <p className="mt-2 text-sm text-slate-600" data-id="element-2349" data-mp="2r74dv90sbjhi">
                    L’activité a été ajoutée à votre carte numérique. Votre facture est disponible dans Mon profil.
                  </p>
                  <div className="mt-6 flex flex-wrap justify-center gap-3" data-id="element-2350" data-mp="3ixabr4hdpadr">
                    <Link to="/ma-carte" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-2351" data-mp="3e728v5m7khzf">
                      <IdCardIcon className="h-4 w-4" aria-hidden="true" data-id="element-2352" data-mp="ajtldq2ylfku" />
                      Voir ma carte
                    </Link>
                    <button type="button" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo" data-id="element-2353" data-mp="2deme1o373izk">
                      <CalendarPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-2354" data-mp="2hg7vlmqoclg3" />
                      Ajouter à mon calendrier
                    </button>
                  </div>
                </div>}
            </div>

            {step < 4 && <div className="mt-8 flex items-center justify-between gap-4" data-id="element-2355" data-mp="3573luffhf8l8">
                <button type="button" onClick={() => step === 0 ? close() : setStep(step - 1)} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2356" data-mp="314yuxogf4q2a">
                  {step === 0 ? 'Annuler' : 'Retour'}
                </button>
                <button type="button" onClick={() => setStep(step + 1)} className="rounded-full bg-ville-indigo px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-2357" data-mp="2lfdzo5cra0a">
                  {step === 3 ? `Payer ${activity.price}` : 'Continuer'}
                </button>
              </div>}

            {step === 4 && <button type="button" onClick={close} className="mt-8 w-full rounded-full border border-slate-200 px-6 py-3 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo" data-id="element-2358" data-mp="310oqisks52vf">
                Terminer
              </button>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}